// src/pages/MyCalendar.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getListingsByUserId } from '../services/ListingsService';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import './MyCalendar.css';

function MyCalendar() {
  const { currentUser } = useAuth();
  const [myListings, setMyListings] = useState([]);
  const [purchasedListings, setPurchasedListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedTab, setSelectedTab] = useState('calendar'); // 'calendar' or 'list'
  const [expandedListings, setExpandedListings] = useState({});

  const getPrimaryLocationText = (listing) => {
    if (!listing) return '';
    if (listing.isVirtual) {
      return 'Virtual Event';
    }
    if (Array.isArray(listing.locations) && listing.locations.length > 0) {
      return listing.locations[0].address || '';
    }
    return listing.location || '';
  };

  // Toggle expanded view for a listing
  const toggleExpanded = (listingId) => {
    setExpandedListings(prev => ({
      ...prev,
      [listingId]: !prev[listingId]
    }));
  };

  // Fetch user's listings and purchased listings
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        console.log("MyCalendar: Fetching data for user:", currentUser.uid);
        
        // Fetch user's listings
        const userListings = await getListingsByUserId(currentUser.uid);
        console.log("MyCalendar: Found user listings:", userListings.length);
        setMyListings(userListings);

        // Fetch user's purchased listings through payments
        const paymentsQuery = query(
          collection(db, 'payments'),
          where('userId', '==', currentUser.uid),
          where('status', '==', 'succeeded')
        );
        
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const userPayments = paymentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));
        
        console.log("MyCalendar: Found user payments:", userPayments.length);
        
        // Log all listing IDs from payments
        const purchasedListingIds = userPayments.map(payment => payment.listingId);
        console.log("MyCalendar: Purchased listing IDs:", purchasedListingIds);
        
        // Get unique listing IDs
        const uniqueListingIds = [...new Set(purchasedListingIds)];
        console.log("MyCalendar: Unique listing IDs:", uniqueListingIds);
        
        // Create a map of payments by listingId for quick lookup
        const paymentsByListing = {};
        userPayments.forEach(payment => {
          if (!paymentsByListing[payment.listingId]) {
            paymentsByListing[payment.listingId] = [];
          }
          paymentsByListing[payment.listingId].push(payment);
        });
        
        // Fetch details for each purchased listing
        const purchasedListingsData = [];
        
        for (const listingId of uniqueListingIds) {
          try {
            console.log(`MyCalendar: Processing listing ID: ${listingId}`);
            
            // Try to get listing details
            const listingRef = doc(db, 'listings', listingId);
            const listingDoc = await getDoc(listingRef);
            
            // Get the most recent payment for this listing
            const paymentsForListing = paymentsByListing[listingId] || [];
            paymentsForListing.sort((a, b) => b.createdAt - a.createdAt);
            const latestPayment = paymentsForListing[0];
            
            if (listingDoc.exists()) {
              console.log(`MyCalendar: Listing ${listingId} exists`);
              // Listing still exists
              purchasedListingsData.push({
                id: listingDoc.id,
                ...listingDoc.data(),
                createdAt: listingDoc.data().createdAt?.toDate(),
                updatedAt: listingDoc.data().updatedAt?.toDate(),
                eventStart: listingDoc.data().eventStart?.toDate() || latestPayment?.eventStart || null,
                eventEnd: listingDoc.data().eventEnd?.toDate() || latestPayment?.eventEnd || null,
                paymentInfo: latestPayment,
                isDeleted: false
              });
            } else {
              console.log(`MyCalendar: Listing ${listingId} is DELETED`);
              // Listing has been deleted - create a placeholder
              purchasedListingsData.push({
                id: listingId,
                title: "Deleted Listing",
                description: "This listing is no longer available.",
                category: "unavailable",
                createdAt: latestPayment?.createdAt || new Date(),
                eventStart: latestPayment?.eventStart || latestPayment?.createdAt || null,
                eventEnd: latestPayment?.eventEnd || null,
                contact: "No contact information available",
                paymentInfo: latestPayment,
                isDeleted: true
              });
            }
          } catch (err) {
            console.error(`Error fetching listing ${listingId}:`, err);
          }
        }
        
        console.log("MyCalendar: Final purchased listings:", purchasedListingsData);
        console.log("MyCalendar: Deleted listings count:", purchasedListingsData.filter(listing => listing.isDeleted).length);
        
        setPurchasedListings(purchasedListingsData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching calendar data:", err);
        setError("Failed to load your calendar data. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // Helper functions for the calendar
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Navigate to previous month
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    return new Date(date).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format price
  const formatPrice = (price) => {
    if (!price && price !== 0) return 'Price on request';
    return `$${parseFloat(price).toFixed(2)} CAD`;
  };

  // Check if a listing has events on a specific day
  const hasEventOnDay = (day) => {
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const events = [];
    
    // Check if any of my listings occur on this day
    myListings.forEach(listing => {
      const start = listing.eventStart || listing.createdAt;
      const end = listing.eventEnd || listing.eventStart || listing.createdAt;
      if (!start) return;
      const s = new Date(start);
      const e = new Date(end);
      const dayStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0);
      const dayEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 23, 59, 59);
      if (s <= dayEnd && e >= dayStart) {
        events.push({ type: 'my-listing', listing });
      }
    });
    
    // Check if any purchased listings occur on this day
    purchasedListings.forEach(listing => {
      const start = listing.eventStart || listing.createdAt || listing.paymentInfo?.createdAt;
      const end = listing.eventEnd || listing.eventStart || listing.createdAt || listing.paymentInfo?.createdAt;
      if (!start) return;
      const s = new Date(start);
      const e = new Date(end);
      const dayStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0);
      const dayEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 23, 59, 59);
      if (s <= dayEnd && e >= dayStart) {
        events.push({ type: 'purchased', listing });
      }
    });
    
    return events.length > 0 ? events : null;
  };

  // Render the calendar
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
    const days = [];
    
    // Week days header
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const events = hasEventOnDay(day);
      days.push(
        <div key={`day-${day}`} className={`calendar-day ${events ? 'has-events' : ''}`}>
          <div className="day-number">{day}</div>
          {events && (
            <div className="day-events">
              {events.map((event, index) => (
                <div 
                  key={index} 
                  className={`event-dot ${event.type === 'my-listing' ? 'my-listing' : 'purchased'} ${event.listing.isDeleted ? 'deleted' : ''}`}
                  title={`${event.listing.title} (${event.type === 'my-listing' ? 'Your listing' : 'Purchased'}${event.listing.isDeleted ? ' - Deleted' : ''})`}
                ></div>
              ))}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="calendar-container">
        <div className="calendar-header">
          <button onClick={prevMonth} className="month-nav-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <h2>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
          <button onClick={nextMonth} className="month-nav-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
        
        <div className="calendar-weekdays">
          {weekDays.map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>
        
        <div className="calendar-days">
          {days}
        </div>
        
        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-dot my-listing"></div>
            <span>Your Listings</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot purchased"></div>
            <span>Purchased Listings</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot deleted"></div>
            <span>Deleted Listings</span>
          </div>
        </div>
      </div>
    );
  };

  // Render the listings in list view
  const renderListings = () => {
    return (
      <div className="calendar-list-view">
        <div className="list-section">
          <h3>Your Listings</h3>
          {myListings.length === 0 ? (
            <p className="empty-list-message">You haven't created any listings yet.</p>
          ) : (
            <div className="listings-list">
              {myListings.map(listing => (
                <div key={listing.id} className="list-item">
                  <div className="list-item-header">
                    <h4>{listing.title}</h4>
                    <span className="list-item-category">{listing.category}</span>
                  </div>
                <p className="list-item-date">
                  {listing.eventStart ? formatDate(listing.eventStart) : (listing.createdAt ? formatDate(listing.createdAt) : 'Date not available')}
                  {listing.eventEnd && (
                    <> – {formatDate(listing.eventEnd)}</>
                  )}
                </p>
                  <Link to={`/listings/${listing.id}`} className="view-listing-link">
                    View Details
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="list-section">
          <h3>Purchased Listings</h3>
          {purchasedListings.length === 0 ? (
            <p className="empty-list-message">You haven't purchased any listings yet.</p>
          ) : (
            <div className="listings-list">
              {purchasedListings.map(listing => {
                const isExpanded = expandedListings[listing.id] || false;
                
                return (
                  <div key={listing.id} className={`list-item purchased ${listing.isDeleted ? 'deleted-listing' : ''}`}>
                    <div className="list-item-header">
                      <h4>
                        {listing.title}
                        {listing.isDeleted && <span className="deleted-listing-tag">Deleted</span>}
                      </h4>
                      <span className="list-item-category">{listing.isDeleted ? 'Unavailable' : listing.category}</span>
                    </div>
                    
                    <div className="list-item-content">
                      <div className="list-item-basics">
                        <p className="list-item-date">
                          <strong>Event Date:</strong> {listing.eventStart ? formatDate(listing.eventStart) : (listing.createdAt ? formatDate(listing.createdAt) : 'Date not available')}
                          {listing.eventEnd && (
                            <> – {formatDate(listing.eventEnd)}</>
                          )}
                        </p>
                        <p className="list-item-price">
                          <strong>Price:</strong> {formatPrice(listing.price)}
                        </p>
                        {listing.paymentInfo && (
                          <p className="list-item-purchased-date">
                            <strong>Purchased:</strong> {listing.paymentInfo.createdAt ? formatDate(listing.paymentInfo.createdAt) : 'Recently'}
                          </p>
                        )}
                      </div>
                      
                      {isExpanded && (
                        <div className="list-item-details">
                          <div className="list-item-description">
                            <h5>Description</h5>
                            <p>{listing.description}</p>
                          </div>
                          
                          {!listing.isDeleted && (
                            <div className="list-item-location">
                              <h5>Location</h5>
                              <p>{getPrimaryLocationText(listing) || 'No location specified'}</p>
                            </div>
                          )}
                          
                          <div className="list-item-contact">
                            <h5>Contact</h5>
                            <p>{listing.contact}</p>
                            {listing.contact && !listing.contact.includes('No contact') && (
                              <a 
                                href={`mailto:${listing.contact}?subject=Regarding ${listing.title}`}
                                className="contact-provider-link"
                              >
                                Contact Provider
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="list-item-actions">
                      <button 
                        onClick={() => toggleExpanded(listing.id)} 
                        className="toggle-details-button"
                      >
                        {isExpanded ? 'Show Less' : 'Show More'}
                      </button>
                      {!listing.isDeleted ? (
                        <Link 
                          to={`/listings/${listing.id}`} 
                          className="view-listing-link"
                          onClick={(e) => {
                            console.log('Clicking listing link:', listing.id);
                          }}
                        >
                          View Full Details
                        </Link>
                      ) : (
                        <span className="view-listing-link disabled">
                          Listing Unavailable
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="calendar-container loading">Loading your calendar...</div>;
  }

  if (error) {
    return <div className="calendar-container error">{error}</div>;
  }

  if (!currentUser) {
    return (
      <div className="calendar-container not-signed-in">
        <p>Please sign in to view your calendar.</p>
      </div>
    );
  }

  return (
    <div className="my-calendar-page">
      <div className="calendar-header-container">
        <h1>My Calendar</h1>
        <p>View your listings and purchased events in one place.</p>
        
        <div className="view-toggle">
          <button 
            className={`view-toggle-button ${selectedTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setSelectedTab('calendar')}
          >
            Calendar View
          </button>
          <button 
            className={`view-toggle-button ${selectedTab === 'list' ? 'active' : ''}`}
            onClick={() => setSelectedTab('list')}
          >
            List View
          </button>
        </div>
      </div>
      
      {selectedTab === 'calendar' ? renderCalendar() : renderListings()}
    </div>
  );
}

export default MyCalendar;
